import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import StatusBadge, { formatCurrency } from "@/components/StatusBadge";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;

export default function PayPublic() {
  const { invoice_id } = useParams();
  const [inv, setInv] = useState(null);
  const [starting, setStarting] = useState(false);

  useEffect(() => {
    axios.get(`${API}/api/paypal/public/invoices/${invoice_id}`)
      .then((r) => setInv(r.data))
      .catch(() => setInv(false));
  }, [invoice_id]);

  const startPayPal = async () => {
    setStarting(true);
    try {
      const { data } = await axios.post(`${API}/api/paypal/public/invoices/${invoice_id}/order`);
      if (data.approval_url) {
        window.location.href = data.approval_url;
      } else {
        toast.error("Unable to start PayPal checkout");
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "PayPal is not available. Please contact us.");
      setStarting(false);
    }
  };

  if (inv === null) return <div className="min-h-screen grid place-items-center text-slate-500">Loading…</div>;
  if (!inv) return <div className="min-h-screen grid place-items-center text-slate-500">Invoice not found.</div>;

  const paid = inv.payment_status === "paid" || inv.outstanding_amount <= 0;

  return (
    <div className="min-h-screen bg-slate-50 py-12 px-6">
      <div className="max-w-xl mx-auto">
        <div className="flex items-center gap-3 mb-8">
          <img
            src="/tecticonism-wordmark-sm.png"
            alt="Tecticonism Studio"
            className="h-7 w-auto object-contain"
          />
          <div className="pl-3 border-l border-slate-200">
            <div className="text-[10px] uppercase tracking-widest text-brand-dark font-medium">Secure Payment</div>
          </div>
        </div>

        <Card className="border-slate-200 shadow-none p-8">
          <div className="text-xs uppercase tracking-wider text-slate-500">Invoice</div>
          <div className="font-heading text-2xl font-semibold mt-1 flex items-center gap-2">
            {inv.invoice_number} <StatusBadge status={inv.payment_status} />
          </div>
          <div className="text-sm text-slate-500 mt-1">
            {inv.client?.firm_name} · <span className="font-mono">{inv.project?.project_code}</span> — {inv.project?.project_name}
          </div>

          <div className="mt-6 grid grid-cols-2 gap-3 text-sm border-t border-slate-100 pt-6">
            <div className="text-slate-500">Invoice Date</div><div className="text-right">{inv.invoice_date?.slice(0, 10)}</div>
            <div className="text-slate-500">Due Date</div><div className="text-right">{inv.due_date?.slice(0, 10)}</div>
            <div className="text-slate-500">Total</div><div className="text-right">{formatCurrency(inv.total_invoice_amount, inv.currency)}</div>
            <div className="text-slate-500">Already received</div><div className="text-right text-emerald-700">{formatCurrency(inv.amount_received, inv.currency)}</div>
            <div className="font-medium">Amount Due</div><div className="text-right font-heading text-xl font-semibold text-slate-900">{formatCurrency(inv.outstanding_amount, inv.currency)}</div>
          </div>

          {paid ? (
            <div className="mt-8 bg-emerald-50 border border-emerald-200 text-emerald-800 rounded-md p-4 text-sm text-center">
              This invoice has been paid in full. Thank you.
            </div>
          ) : (
            <div className="mt-8 space-y-3">
              <Button
                data-testid="paypal-pay-btn"
                onClick={startPayPal}
                disabled={starting}
                className="w-full bg-[#0070ba] hover:bg-[#005a99] text-white h-12 text-base"
              >
                {starting ? "Redirecting…" : "Pay with PayPal"}
              </Button>
              <div className="text-xs text-slate-500 text-center">
                You'll be redirected to PayPal to complete the payment securely. Amount will be charged in {inv.currency}.
              </div>
            </div>
          )}
        </Card>

        <div className="text-center text-xs text-slate-400 mt-6">
          © {new Date().getFullYear()} Tecticonism Studio LLC — Powered by PayPal.
        </div>
      </div>
    </div>
  );
}
